/* Progressive enhancement: the complete rich text is rendered by Liquid once.
   Without JavaScript it remains readable. Only one version is visible at a time. */
if (!customElements.get('midec-category-description')) {
  customElements.define('midec-category-description', class extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('.midec-description__content');
      this.preview = this.querySelector('.midec-description__preview');
      this.button = this.querySelector('.midec-description__toggle');
      if (!this.content || !this.preview || !this.button) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.expanded = this.dataset.startCollapsed !== 'true';
      this.desktopMedia = window.matchMedia('(min-width: 750px)');
      this.desktopMedia.addEventListener('change', () => this.showState(), { signal });
      this.button.addEventListener('click', () => {
        this.expanded = !this.expanded;
        this.showState();
        // Keep the same keyboard target in view after a long description closes.
        if (!this.expanded && this.button.getBoundingClientRect().top < 0) {
          this.button.scrollIntoView({ block: 'nearest', behavior: 'instant' });
        }
      }, { signal });

      this.buildPreview();
      this.measure();
      this.resizeObserver = new ResizeObserver(([entry]) => {
        if (entry.contentRect.width === this.lastWidth) return;
        this.lastWidth = entry.contentRect.width;
        this.measure();
      });
      this.resizeObserver.observe(this);
      this.content.addEventListener('load', () => this.measure(), { capture: true, signal });
      document.fonts?.ready.then(() => { if (this.isConnected) this.measure(); });

      // Horizon's optional infinite scrolling changes the URL after appending cards.
      // Observe its result list without modifying navigation, history or grid code.
      if (this.dataset.collection === 'true') {
        this.updatePageVisibility = () => {
          const page = Number(new URL(window.location.href).searchParams.get('page') || 1);
          const wrapper = this.closest('.midec-description-section');
          if (wrapper) wrapper.hidden = page > 1;
        };
        this.pageObserver = new MutationObserver(this.updatePageVisibility);
        const results = document.querySelector('results-list');
        if (results) this.pageObserver.observe(results, { childList: true, subtree: true });
        window.addEventListener('popstate', this.updatePageVisibility, { signal });
        document.addEventListener('filter:update', this.updatePageVisibility, { signal });
        this.updatePageVisibility();
      }
    }

    buildPreview() {
      // Clone a short rich-text range, retaining paragraphs, emphasis and links.
      // This excerpt is created in the browser, never duplicated in the source HTML.
      const walker = document.createTreeWalker(this.content, NodeFilter.SHOW_TEXT);
      let remaining = 320;
      let node;
      let lastNode;
      let offset = 0;
      while ((node = walker.nextNode())) {
        if (node.parentElement.closest('script, style')) continue;
        lastNode = node;
        if (node.textContent.length > remaining) {
          const endOfWord = node.textContent.indexOf(' ', remaining);
          offset = endOfWord >= 0 && endOfWord < remaining + 40 ? endOfWord : remaining;
          break;
        }
        remaining -= node.textContent.length;
        offset = node.textContent.length;
      }
      this.preview.replaceChildren();
      if (!lastNode) return;
      const range = document.createRange();
      range.setStart(this.content, 0);
      range.setEnd(lastNode, offset);
      this.preview.append(range.cloneContents());
      this.preview.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
      this.preview.querySelectorAll('script, style, img, video, audio, iframe, form').forEach(element => element.remove());
      if (node) {
        const textNodes = document.createTreeWalker(this.preview, NodeFilter.SHOW_TEXT);
        let finalText;
        while (textNodes.nextNode()) finalText = textNodes.currentNode;
        if (finalText) finalText.textContent = finalText.textContent.trimEnd() + '…';
      }
    }

    measure() {
      this.content.hidden = false;
      this.isLong = this.content.scrollHeight > 240 || this.content.textContent.trim().length > 600;
      this.showState();
    }

    showState() {
      const desktop = this.desktopMedia.matches;
      const open = desktop || !this.isLong || this.expanded;
      this.content.hidden = !open;
      this.preview.hidden = open;
      this.button.hidden = desktop || !this.isLong;
      this.button.setAttribute('aria-expanded', String(open));
      this.button.querySelector('[data-more]').hidden = open;
      this.button.querySelector('[data-less]').hidden = !open;
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.pageObserver?.disconnect();
    }
  });
}
